import { useState } from "react";
import { BulkSelectionControl } from "@design-system/BulkSelectionControl/BulkSelectionControl";
import { Checkbox } from "@design-system/Checkbox/Checkbox";
import { ToggleGroup } from "@design-system/ToggleGroup/ToggleGroup";
import { SandboxLayout } from "./SandboxLayout";
import { ShowcasePreview } from "./ShowcasePreview";
import styles from "./FiltersToolbarPlayground.module.scss";

type SelectionState = "none" | "some" | "all";
type ControlSize = "md" | "lg";

const transactions = [
    {
        id: "txn-1",
        name: "Adobe",
        details: "Software subscriptions · Aug 8",
        amount: "$59.99",
    },
    {
        id: "txn-2",
        name: "Delta Air Lines",
        details: "Travel · Aug 7",
        amount: "$428.16",
    },
    {
        id: "txn-3",
        name: "Stripe payout",
        details: "Income · Aug 6",
        amount: "$2,840.00",
    },
    {
        id: "txn-4",
        name: "WeWork",
        details: "Rent and lease · Aug 5",
        amount: "$650.00",
    },
];

const someTransactionIds = transactions.slice(0, 2).map(({ id }) => id);
const allTransactionIds = transactions.map(({ id }) => id);

const getSelectionState = (selectedCount: number): SelectionState => {
    if (selectedCount === 0) {
        return "none";
    }
    if (selectedCount === transactions.length) {
        return "all";
    }
    return "some";
};

export const BulkSelectionControlPlayground = () => {
    const [selectedTransactionIds, setSelectedTransactionIds] =
        useState<string[]>(someTransactionIds);
    const [size, setSize] = useState<ControlSize>("lg");

    const selectedCount = selectedTransactionIds.length;
    const selectionState = getSelectionState(selectedCount);

    const setSelectionState = (state: SelectionState) => {
        if (state === "none") {
            setSelectedTransactionIds([]);
        } else if (state === "some") {
            setSelectedTransactionIds(someTransactionIds);
        } else {
            setSelectedTransactionIds(allTransactionIds);
        }
    };

    const toggleTransaction = (transactionId: string) => {
        setSelectedTransactionIds((currentIds) =>
            currentIds.includes(transactionId)
                ? currentIds.filter((id) => id !== transactionId)
                : [...currentIds, transactionId],
        );
    };

    return (
        <SandboxLayout
            controls={
                <>
                    <section>
                        <h2>Selection state</h2>
                        <ToggleGroup.Root
                            value={selectionState}
                            onValueChange={(value) => {
                                if (
                                    value === "none" ||
                                    value === "some" ||
                                    value === "all"
                                ) {
                                    setSelectionState(value);
                                }
                            }}
                        >
                            <ToggleGroup.Item value="none">
                                None
                            </ToggleGroup.Item>
                            <ToggleGroup.Item value="some">
                                Some
                            </ToggleGroup.Item>
                            <ToggleGroup.Item value="all">All</ToggleGroup.Item>
                        </ToggleGroup.Root>
                    </section>
                    <section>
                        <h2>Size</h2>
                        <ToggleGroup.Root
                            value={size}
                            onValueChange={(value) => {
                                if (value === "md" || value === "lg") {
                                    setSize(value);
                                }
                            }}
                        >
                            <ToggleGroup.Item value="lg">
                                Large
                            </ToggleGroup.Item>
                            <ToggleGroup.Item value="md">
                                Medium
                            </ToggleGroup.Item>
                        </ToggleGroup.Root>
                    </section>
                </>
            }
        >
            <ShowcasePreview
                summary={`${selectionState} selected`}
                output={JSON.stringify(
                    {
                        props: {
                            selectedCount,
                            size,
                        },
                        showcaseState: {
                            selectionState,
                            selectedTransactionIds,
                        },
                    },
                    null,
                    2,
                )}
            >
                <div className={styles.selectionSurface}>
                    <div className={styles.selectionHeader}>
                        <Checkbox
                            label="Select all transactions"
                            checked={selectionState === "all"}
                            indeterminate={selectionState === "some"}
                            onCheckedChange={() =>
                                setSelectionState(
                                    selectionState === "all" ? "none" : "all",
                                )
                            }
                        />
                        <h2 className={styles.selectionTitle}>
                            August transactions
                        </h2>
                    </div>
                    <div className={styles.transactionList}>
                        {transactions.map((transaction) => (
                            <div
                                key={transaction.id}
                                className={styles.transactionRow}
                            >
                                <Checkbox
                                    checked={selectedTransactionIds.includes(
                                        transaction.id,
                                    )}
                                    onCheckedChange={() =>
                                        toggleTransaction(transaction.id)
                                    }
                                    label={
                                        <span
                                            className={
                                                styles.transactionDetails
                                            }
                                        >
                                            <span
                                                className={
                                                    styles.transactionName
                                                }
                                            >
                                                {transaction.name}
                                            </span>
                                            <span
                                                className={
                                                    styles.transactionMeta
                                                }
                                            >
                                                {transaction.details}
                                            </span>
                                        </span>
                                    }
                                />
                                <span className={styles.transactionAmount}>
                                    {transaction.amount}
                                </span>
                            </div>
                        ))}
                    </div>
                    <div className={styles.selectionFooter}>
                        <BulkSelectionControl
                            selectedCount={selectedCount}
                            onClearSelection={() =>
                                setSelectedTransactionIds([])
                            }
                            size={size}
                        />
                    </div>
                </div>
            </ShowcasePreview>
        </SandboxLayout>
    );
};
